import { graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { MediaContext } from '../context/MediaContext'
import LineCanvas from '../ui/canvas/LineCanvas'
import { frontpagePreset } from '../ui/canvas/presets/frontpage'
import { tripleStripsBaseSize, tripleStripsPreset } from '../ui/canvas/presets/tripleStrips'
import StageCanvas from '../ui/canvas/StageCanvas'
import CaptionBox from '../ui/components/CaptionBox'
import CaseTeaserGallery from '../ui/components/CaseTeaserGallery'
import Heading from '../ui/components/Heading'
import ImageTeaser from '../ui/components/ImageTeaser'
import JobList from '../ui/components/JobList'
import Paragraph from '../ui/components/Paragraph'
import SEO from '../ui/components/Seo/Seo'
import Stage from '../ui/components/Stage'
import ParallaxEffect from '../ui/effects/ParallaxEffect'
import Banner from '../ui/layouts/Banner'
import Column from '../ui/layouts/Column'
import Flex from '../ui/layouts/Flex'
import Hide from '../ui/layouts/Hide'
import Position from '../ui/layouts/Position'
import Row from '../ui/layouts/Row'
import Section from '../ui/layouts/Section'
import Spacer from '../ui/layouts/Spacer'
import TemplateWrapper from '../ui/templates/TemplateWrapper'

const IndexPage = ({
  location,
  pageContext: { langKey, jobs = [], cases = [] },
  data: { teamImage, standupImage, workshopImage, helloImage }
}) => {
  const { breakpoint } = useContext(MediaContext)

  return (
    <TemplateWrapper
      location={location}
      lang={langKey}
      nextPage={{ to: '/agentur', title: 'Unsere Agentur – Heartworking seit 2010' }}
    >
      <SEO
        title="Startseite"
        description="Neoskop ist eine Digitalagentur aus Hannover. Wir entwickeln digitale Produkte. Exzellent in Kreation und Technik. Strategisch, agil und mit Herzblut. Für einfach glücklichere Kunden."
        frontpage
      />
      <Stage
        headline="Wir sind Architektinnen & Baumeister für den digitalen Raum."
        subheadline="Digital&nbsp;only. User&nbsp;first. Lead&nbsp;centric."
        indicatorLabel="Neoskop entdecken"
      >
        <StageCanvas lines={frontpagePreset} dashValue={1} />
      </Stage>
      <Section id="imageIntro" mobilePadding={false}>
        <Row width="large" noMobilePadding>
          <Column span={12} spanWhen={{ lg: 7 }} gutter={false} gutterWhen={{ md: true }}>
            <GatsbyImage image={standupImage.childImageSharp.gatsbyImageData} alt="Neoskop in Aktion - Unser Standup" />
            <Position
              variant="absolute"
              width="58%"
              bottom="-10%"
              bottomWhen={{ md: '-10%' }}
              right="0%"
              rightWhen={{ md: '-2%', lg: '-6%' }}
            >
              <ParallaxEffect speed={0.3}>
                <GatsbyImage
                  image={workshopImage.childImageSharp.gatsbyImageData}
                  alt="Neoskop in Aktion - Unser Standup"
                />
              </ParallaxEffect>
            </Position>
          </Column>
          <Column span={0} spanWhen={{ lg: 4 }} shiftWhen={{ hg: 'h' }} offsetWhen={{ lg: 1 }}>
            <Hide breakpoint="md" direction="max">
              <ParallaxEffect speed={-0.3}>
                <GatsbyImage image={helloImage.childImageSharp.gatsbyImageData} alt="Hello Post-It" />
              </ParallaxEffect>
            </Hide>
          </Column>
        </Row>
      </Section>
      <Section distance="huge">
        <Row width="content">
          <Column span={12}>
            <Paragraph variant="hero">
              Neoskop ist eine <strong>inhaber&shy;geführte Digital&shy;agentur aus Hannover</strong> mit der Mission,
              Ihrer Zielgruppe ein digitales Zuhause zu geben und der Vision gemeinsam mit Ihnen neue Branchenmaßstäbe
              zu setzen.
            </Paragraph>
            <Paragraph variant="hero">
              Dafür braucht es fähige und leidenschaftliche Menschen in inter­disziplinären Teams, ein kreatives &
              agiles Mindset und den Mut, neue Wege zu gehen.
            </Paragraph>
            <Paragraph variant="hero">
              <strong>Wir nennen das Heartworking.</strong>
            </Paragraph>
          </Column>
        </Row>
      </Section>
      <Section distance="default">
        <CaseTeaserGallery caseStudies={cases.slice(0, 4).reverse()} />
      </Section>
      <Banner id="jobs" hideOverflow>
        <Hide breakpoint="md" direction="max">
          <Position variant="absolute" right="0%" rightWhen={{ md: '-200px' }} bottom="-10px">
            <LineCanvas size={breakpoint.lg ? 900 : 600} preset={tripleStripsPreset} baseSize={tripleStripsBaseSize} />
          </Position>
        </Hide>
        <Row align="stretch" width="large">
          <Column span={12} spanWhen={{ md: 6, lg: 5 }} order={2} orderWhen={{ md: 1 }}>
            <Spacer size="l" when={{ md: 'z' }} />
            <Flex direction="column" justify="space-between">
              <div>
                <Heading level={2} invert>
                  Du liebst digitale Produkte und die Arbeit im Team?
                </Heading>
                <Paragraph variant="hero" invert noMargin>
                  Dann schau doch mal bei unseren offenen Stellen oder direkt bei uns auf einen Cappuccino oder eine
                  Krökelrunde vorbei.
                </Paragraph>
                <Paragraph variant="hero" invert>
                  Wir freuen uns auf dich!
                </Paragraph>
              </div>
              <Spacer size="m" when={{ md: 'xl' }} />
              <CaptionBox caption="Neue Stellen" invert>
                <Spacer size="xxs" when={{ md: 'xs' }} />
                <JobList jobs={jobs} />
              </CaptionBox>
              <Hide breakpoint="md" direction="min">
                <Spacer size="m" when={{ md: 'z' }} />
              </Hide>
            </Flex>
          </Column>
          <Column
            span={12}
            spanWhen={{ md: 5 }}
            order={1}
            orderWhen={{ md: 2 }}
            gutter={false}
            gutterWhen={{ md: true }}
            offsetWhen={{ md: 1, lg: 2 }}
          >
            <ImageTeaser
              file={teamImage}
              label="Arbeiten bei Neoskop"
              to="/jobs/"
              alt="Das Neoskop-Team auf der Standup-Treppe"
            />
          </Column>
        </Row>
      </Banner>
    </TemplateWrapper>
  )
}

export const pageQuery = graphql`
  {
    teamImage: file(relativePath: { eq: "team/team-1.jpg" }) {
      childImageSharp {
        gatsbyImageData(quality: 75, layout: FULL_WIDTH)
      }
    }
    standupImage: file(relativePath: { eq: "agency-life/standup/standup-1.jpg" }) {
      childImageSharp {
        gatsbyImageData(quality: 75, layout: FULL_WIDTH, aspectRatio: 1.5)
      }
    }
    workshopImage: file(relativePath: { eq: "at-work/arbeit-7.jpg" }) {
      childImageSharp {
        gatsbyImageData(quality: 75, layout: FULL_WIDTH)
      }
    }
    helloImage: file(relativePath: { eq: "agency/agentur-1.jpg" }) {
      childImageSharp {
        gatsbyImageData(quality: 75, layout: FULL_WIDTH)
      }
    }
  }
`

IndexPage.propTypes = {
  location: PropTypes.object.isRequired,
  pageContext: PropTypes.object,
  data: PropTypes.object
}

export default IndexPage
